import { createBrowserRouter, Navigate } from 'react-router'
import { lazy, Suspense } from 'react'
import { PublicLayout } from '@/layouts/PublicLayout'
import { AppLayout } from '@/layouts/AppLayout'
import { LoadingScreen } from '@/components/ui/LoadingScreen'

// ─── Lazy-loaded public pages ──────────────────────────────────────────────────
const LandingPage        = lazy(() => import('@/pages/public/LandingPage'))
const CalculatorPage     = lazy(() => import('@/pages/public/CalculatorPage'))
const PricingPage        = lazy(() => import('@/pages/public/PricingPage'))
const ExampleOrderPage   = lazy(() => import('@/pages/public/ExampleOrderPage'))
const SignInPage         = lazy(() => import('@/pages/public/SignInPage'))
const PrivacyPage        = lazy(() => import('@/pages/public/PrivacyPage'))
const TermsPage          = lazy(() => import('@/pages/public/TermsPage'))
const SecurityPage       = lazy(() => import('@/pages/public/SecurityPage'))
const PublicApprovalPage = lazy(() => import('@/pages/public/PublicApprovalPage'))
const ApprovalConfirmedPage = lazy(() => import('@/pages/public/ApprovalConfirmedPage'))
const ExpiredLinkPage    = lazy(() => import('@/pages/public/ExpiredLinkPage'))
const NotFoundPage       = lazy(() => import('@/pages/public/NotFoundPage'))

// ─── Lazy-loaded authenticated pages ─────────────────────────────────────────
const OnboardingPage     = lazy(() => import('@/pages/app/OnboardingPage'))
const DashboardPage      = lazy(() => import('@/pages/app/DashboardPage'))
const CustomersPage      = lazy(() => import('@/pages/app/CustomersPage'))
const CustomerDetailPage = lazy(() => import('@/pages/app/CustomerDetailPage'))
const ProjectsPage       = lazy(() => import('@/pages/app/ProjectsPage'))
const ProjectDetailPage  = lazy(() => import('@/pages/app/ProjectDetailPage'))
const NewChangeOrderPage = lazy(() => import('@/pages/app/NewChangeOrderPage'))
const ChangeOrderEditorPage  = lazy(() => import('@/pages/app/ChangeOrderEditorPage'))
const ChangeOrderPreviewPage = lazy(() => import('@/pages/app/ChangeOrderPreviewPage'))
const ChangeOrderStatusPage  = lazy(() => import('@/pages/app/ChangeOrderStatusPage'))
const TemplatesPage      = lazy(() => import('@/pages/app/TemplatesPage'))
const BillingPage        = lazy(() => import('@/pages/app/BillingPage'))
const SettingsPage       = lazy(() => import('@/pages/app/SettingsPage'))
const DataExportPage     = lazy(() => import('@/pages/app/DataExportPage'))
const RequestIntakePage  = lazy(() => import('@/pages/app/RequestIntakePage'))
const RequestStatusPage  = lazy(() => import('@/pages/app/RequestStatusPage'))
const OperatorDashboardPage = lazy(() => import('@/pages/operator/OperatorDashboardPage'))
const OperatorQueuePage     = lazy(() => import('@/pages/operator/OperatorQueuePage'))
const OperatorReviewPage    = lazy(() => import('@/pages/operator/OperatorReviewPage'))

import { useAuth } from '@/contexts/AuthContext'
function OperatorRouteGuard({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuth()
  if (loading || !profile) return <LoadingScreen />
  if (profile.role !== 'operator' && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
}

import React from 'react'

export const router = createBrowserRouter([
  // ─── Public routes ─────────────────────────────────────────────────────────
  {
    element: <PublicLayout />,
    children: [
      { path: '/',            element: <S><LandingPage /></S> },
      { path: '/calculator',  element: <S><CalculatorPage /></S> },
      { path: '/pricing',     element: <S><PricingPage /></S> },
      { path: '/example',     element: <S><ExampleOrderPage /></S> },
      { path: '/sign-in',     element: <S><SignInPage /></S> },
      { path: '/privacy',     element: <S><PrivacyPage /></S> },
      { path: '/terms',       element: <S><TermsPage /></S> },
      { path: '/security',    element: <S><SecurityPage /></S> },
      { path: '/expired',     element: <S><ExpiredLinkPage /></S> },
    ],
  },
  // ─── Public approval (no nav) ──────────────────────────────────────────────
  { path: '/approval/:token',   element: <S><PublicApprovalPage /></S> },
  { path: '/approval/confirmed', element: <S><ApprovalConfirmedPage /></S> },
  // ─── Authenticated app routes ──────────────────────────────────────────────
  {
    element: <AppLayout />,
    children: [
      { path: '/onboarding',                              element: <S><OnboardingPage /></S> },
      { path: '/dashboard',                               element: <S><DashboardPage /></S> },
      { path: '/customers',                               element: <S><CustomersPage /></S> },
      { path: '/customers/:id',                           element: <S><CustomerDetailPage /></S> },
      { path: '/projects',                                element: <S><ProjectsPage /></S> },
      { path: '/projects/:id',                            element: <S><ProjectDetailPage /></S> },
      { path: '/change-orders/new',                       element: <S><NewChangeOrderPage /></S> },
      { path: '/change-orders/:id/edit',                  element: <S><ChangeOrderEditorPage /></S> },
      { path: '/change-orders/:id/preview',               element: <S><ChangeOrderPreviewPage /></S> },
      { path: '/change-orders/:id/status',                element: <S><ChangeOrderStatusPage /></S> },
      { path: '/templates',                               element: <S><TemplatesPage /></S> },
      { path: '/billing',                                 element: <S><BillingPage /></S> },
      { path: '/settings',                                element: <S><SettingsPage /></S> },
      { path: '/settings/data',                           element: <S><DataExportPage /></S> },
      { path: '/requests/new',                            element: <S><RequestIntakePage /></S> },
      { path: '/requests/:id/status',                     element: <S><RequestStatusPage /></S> },
      { path: '/operator/dashboard',                      element: <OperatorRouteGuard><S><OperatorDashboardPage /></S></OperatorRouteGuard> },
      { path: '/operator/queue',                          element: <OperatorRouteGuard><S><OperatorQueuePage /></S></OperatorRouteGuard> },
      { path: '/operator/review/:id',                     element: <OperatorRouteGuard><S><OperatorReviewPage /></S></OperatorRouteGuard> },
      { path: '/account',                                 element: <Navigate to="/settings" replace /> },
      { path: '/app',                                     element: <Navigate to="/dashboard" replace /> },
    ],
  },
  // ─── 404 ──────────────────────────────────────────────────────────────────
  { path: '*', element: <S><NotFoundPage /></S> },
])
